<template>
  <div
    ref="VisionSearchSelect"
    class="VisionSearchSelect"
    :class="[`VisionSearchSelect--${shape}`]"
  >
    <VisionTextInput
      :id="id"
      ref="VisionSearchSelectInput"
      :name="name"
      :label="label"
      :error="error"
      :hint="calculatedHint"
      :label-hint="labelHint"
      :shape="shape"
      :disabled="disabled"
      :cursor="showOptions ? 'default' : 'pointer'"
      icon="chevron"
      :icon-rotation="90"
      autocomplete="off"
      :value="textSearchInitiated ? searchValue : selectedValue.displayValue"
      :class="'VisionSearchSelect__input'"
      @update:value="handleSearchInputUpdate"
      @keydown="handleKeyNavigation"
      @focus="handleFocus"
      @blur="handleBlur"
      @enter="handleInputEnter"
      @escape="handleEscape"
    />
    <div
      v-if="options.length"
      v-show="searchValue ? queryMatchingOptions : showOptions"
      class="VisionSearchSelect__suggestions"
    >
      <ul class="VisionSearchSelect__suggestions-list">
        <li
          v-for="option in optionsToShow"
          :key="option.value"
          ref="option"
          :data-value="option.value"
          class="VisionSearchSelect__suggestion"
          :class="[
            {
              'VisionSearchSelect__suggestion--in-focus':
                option.value === optionInFocus.value && showOptions,
              'VisionSearchSelect__suggestion--divider': option.divider,
              'VisionSearchSelect__suggestion--disabled': option.disabled,
            },
          ]"
          @click="handleSelectedOption(option.value)"
        >
          {{ option.displayValue }}
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import VisionTextInput from '../VisionTextInput/VisionTextInput.vue'

export default {
  name: 'VisionSearchSelect',

  components: {
    VisionTextInput,
  },

  props: {
    id: {
      type: String,
      required: true,
    },
    name: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      required: false,
      default: '',
    },
    error: {
      type: String,
      default: '',
      required: false,
    },
    hint: {
      type: String,
      default: '',
      required: false,
    },
    labelHint: {
      type: String,
      default: '',
      required: false,
    },
    shape: {
      type: String,
      default: 'square',
      validator: (value) => {
        return ['rounded', 'square'].includes(value)
      },
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    options: {
      type: Array,
      required: true,
    },
  },

  emits: ['selectedOption'],

  data() {
    return {
      optionInFocus: {},
      optionIndexInFocus: null,
      showOptions: false,
      showOptionsOnFocus: true,
      textSearchInitiated: false,
      searchValue: '',
      queryMatchingOptions: [],
    }
  },

  computed: {
    calculatedHint() {
      if (this.showOptions) {
        return ''
      }

      return this.hint
    },

    selectedValue() {
      if (this.searchValue) {
        return this.searchValue
      }

      let selected = this.optionsToShow.filter((option) => option.selected)

      return selected.length ? selected[0] : this.optionsToShow[0]
    },

    optionsToShow() {
      return this.searchValue ? this.queryMatchingOptions : this.options
    },
  },

  watch: {
    showOptions(newValue) {
      if (!newValue) {
        this.optionIndexInFocus = -1
      }
    },
  },

  mounted() {
    document.addEventListener('click', this.handleExternalClick)
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleExternalClick)
  },

  methods: {
    handleFocus() {
      if (this.showOptionsOnFocus) {
        this.showOptions = true
        this.optionIndexInFocus = -1
      }
    },
    handleSelectedOption(e) {
      this.$emit('selectedOption', e)

      this.showOptions = false
      this.searchValue = ''
      this.textSearchInitiated = false
      this.optionInFocus = {}
      this.focusOutOnOption(e)
    },
    handleSearchInputUpdate(e) {
      this.textSearchInitiated = true
      this.searchValue = e

      const queryMatchingOptions = []

      this.options.forEach((option) => {
        if (
          option.displayValue
            ?.toLowerCase()
            .includes(this.searchValue.toLowerCase())
        ) {
          queryMatchingOptions.push(option)
        }
      })

      // This removes any duplicate values from displaying on search
      this.queryMatchingOptions = Object.values(
        queryMatchingOptions.reduce(
          (acc, obj) => ({ ...acc, [obj.value]: obj }),
          {},
        ),
      )
    },
    handleExternalClick(e) {
      if (this.$refs.VisionSearchSelect.contains(e.target)) return

      this.showOptions = false
    },
    handleKeyNavigation(e) {
      switch (e.keyCode) {
        // arrow up
        case 38:
          e.preventDefault()
          this.showOptions = true
          if (this.optionIndexInFocus > 0) {
            /**
             *
             * If the next option is a divider, skip it
             *
             */
            if (
              this.optionsToShow[this.optionIndexInFocus - 1].divider &&
              this.optionsToShow[this.optionIndexInFocus - 1].disabled
            ) {
              this.optionIndexInFocus -= 2
            } else {
              this.optionIndexInFocus--
            }
            this.optionInFocus = this.optionsToShow[this.optionIndexInFocus]
            this.focusOnOption()
          }

          break
        // arrow down
        case 40:
          e.preventDefault()
          this.showOptions = true
          if (this.optionIndexInFocus < this.optionsToShow.length - 1) {
            /**
             *
             * If the next option is a divider, skip it
             *
             */
            if (
              this.optionsToShow[this.optionIndexInFocus + 1].divider ||
              this.optionsToShow[this.optionIndexInFocus + 1].disabled
            ) {
              this.optionIndexInFocus += 2
            } else {
              this.optionIndexInFocus++
            }

            this.optionInFocus = this.optionsToShow[this.optionIndexInFocus]
            this.focusOnOption()
          }

          break
        // enter
        case 13:
          if (!this.optionInFocus.value) return
          this.handleSelectedOption(this.optionInFocus.value)
          break
        // escape
        case 27:
          this.showOptions = false
          this.searchValue = false
          this.textSearchInitiated = false
      }
    },
    handleBlur() {
      setTimeout(() => {
        this.textSearchInitiated = false
        this.searchValue = ''
      }, 50)
    },
    handleInputEnter() {
      if (this.showOptions && this.queryMatchingOptions.length) {
        this.handleSelectedOption(this.queryMatchingOptions[0].value)
      }
    },
    handleEscape() {
      this.showOptions = false
    },
    focusOnOption() {
      this.$refs.option.forEach((option) => {
        if (option.dataset.value === this.optionInFocus) {
          option.focus()
        }
      })
    },
    focusOutOnOption(selectedValue) {
      this.$refs.option.forEach((option) => {
        if (option.dataset.value === selectedValue) {
          option.blur()
        }
      })
    },
  },
}
</script>

<style lang="postcss">
.VisionSearchSelect {
  @apply relative;

  --vision-dropdown-max-height: var(--vision-dropdown-max-height, none);
}

.VisionSearchSelect__input {
  @apply relative z-[2];
}

.VisionSearchSelect__suggestions {
  @apply bg-white border border-grey-light border-t-0 w-full absolute -z--1 -mt-1 max-h-[var(--vision-dropdown-max-height)] overflow-auto shadow-md;
}

.VisionSearchSelect__suggestion {
  @apply border-b border-grey-light py-2 px-2 cursor-pointer focus:bg-grey-light hover:bg-grey-light;
}

.VisionSearchSelect__suggestion--in-focus {
  @apply bg-grey-light;
}

.VisionSearchSelect__suggestion--divider {
  @apply font-bold py-4 cursor-default;
}

.VisionSearchSelect__suggestion--disabled {
  @apply bg-grey-light opacity-40 pointer-events-none;
}

/**

Modifiers

--rounded

*/

.VisionSearchSelect--rounded {
  .VisionSearchSelect__suggestions-list {
    @apply top-[calc(100%-28px)];

    .VisionSearchSelect__suggestion:first-child {
      @apply pt-9;
    }
  }

  .VisionSearchSelect__suggestions {
    @apply -mt-7;
  }
}
</style>
